import axios from "services/axiosConfig";
import authHeader from "services/auth-header";
import { API_URL } from "utils/constants";

const getRules = async ({
  sortField = "",
  sortDirection = "",
  name = "",
  isActive = false,
  mine = false,
  limit = 20,
  offset = 0,
} = {}) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (sortField && sortDirection) {
    params.append("sort_field", sortField);
    params.append("sort_direction", sortDirection);
  }

  // Server-side name/ID filter (the backend matches rule name and id).
  if (name) {
    params.append("name", name);
  }

  // "Show active only": send is_active=true when checked; omit when unchecked
  // so the server returns both active and inactive rules.
  if (isActive) {
    params.append("is_active", "true");
  }

  // "Show My Criterias": restrict to the acting user's own rules.
  if (mine) {
    params.append("mine", "true");
  }

  const response = await axios.get(
    API_URL + `rfq/notification-criteria/?${params}`,
    {
      headers: authHeader(),
    },
  );
  return response.data;
};

const createRule = async (ruleData) => {
  const response = await axios.post(API_URL + "rfq/notification-criteria/", ruleData, {
    headers: authHeader(),
  });
  return response.data;
};

const updateRule = async (id, ruleData) => {
  const response = await axios.put(API_URL + `rfq/notification-criteria/${id}/`, ruleData, {
    headers: authHeader(),
  });
  return response.data;
};

const deleteRule = async (id) => {
  const response = await axios.delete(API_URL + `rfq/notification-criteria/${id}/`, {
    headers: authHeader(),
  });
  return response.data;
};

const getContactsWithStats = async (searchQuery = '', limit = 20, offset = 0, hasRecentActivity = true, sourceIds = [], sortField = '', sortDirection = '', signal = null) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    has_recent_activity: Boolean(hasRecentActivity).toString(),
  });

  if (searchQuery) {
    params.append('search', searchQuery);
  }

  if (sourceIds && sourceIds.length > 0) {
    params.append('source_id', sourceIds.join(','));
  }

  if (sortField && sortDirection) {
    params.append('sort_field', sortField);
    params.append('sort_direction', sortDirection);
  }

  const config = { headers: authHeader() };
  if (signal) {
    config.signal = signal;
  }

  const response = await axios.get(API_URL + `rfq/contacts/deduplicated/?${params}`, config);
  return response.data;
};

const getSelectedContacts = async (contactIds, limit = 20, offset = 0, searchQuery = '', hasRecentActivity = true, sourceIds = [], sortField = '', sortDirection = '', signal = null) => {
  if (!contactIds || contactIds.length === 0) {
    return { results: [], count: 0 };
  }

  const params = new URLSearchParams({
    search_by_id: contactIds.join(','),
    limit: limit.toString(),
    offset: offset.toString(),
    has_recent_activity: Boolean(hasRecentActivity).toString(),
  });

  if (searchQuery) {
    params.append('search', searchQuery);
  }

  if (sourceIds && sourceIds.length > 0) {
    params.append('source_id', sourceIds.join(','));
  }

  if (sortField && sortDirection) {
    params.append('sort_field', sortField);
    params.append('sort_direction', sortDirection);
  }

  const config = { headers: authHeader() };
  if (signal) {
    config.signal = signal;
  }

  const response = await axios.get(API_URL + `rfq/contacts/deduplicated/?${params}`, config);
  return response.data;
};

const testRule = async (ruleId) => {
  const response = await axios.get(API_URL + `rfq/notification-criteria/${ruleId}/test-criteria/`, {
    headers: authHeader(),
  });
  return response.data;
};

const getRuleDetails = async (id) => {
  const response = await axios.get(API_URL + `rfq/notification-criteria/${id}/`, {
    headers: authHeader(),
  });
  return response.data;
};

const getRfqSources = async () => {
  const response = await axios.get(API_URL + "rfq/sources/", {
    headers: authHeader(),
  });
  return response.data;
};

// Schedule management endpoints
const getSchedules = async (criteriaId) => {
  const response = await axios.get(API_URL + `rfq/notification-criteria/${criteriaId}/schedules/`, {
    headers: authHeader(),
  });
  return response.data;
};

const createSchedule = async (criteriaId, scheduleData) => {
  const response = await axios.post(API_URL + `rfq/notification-criteria/${criteriaId}/schedules/`, scheduleData, {
    headers: authHeader(),
  });
  return response.data;
};

const updateSchedule = async (criteriaId, scheduleId, scheduleData) => {
  const response = await axios.put(API_URL + `rfq/notification-criteria/${criteriaId}/schedules/${scheduleId}/`, scheduleData, {
    headers: authHeader(),
  });
  return response.data;
};

const deleteSchedule = async (criteriaId, scheduleId) => {
  const response = await axios.delete(API_URL + `rfq/notification-criteria/${criteriaId}/schedules/${scheduleId}/`, {
    headers: authHeader(),
  });
  return response.data;
};

const bulkCreateSchedules = async (criteriaId, schedulesData) => {
  const response = await axios.post(API_URL + `rfq/notification-criteria/${criteriaId}/schedules/bulk-create/`, schedulesData, {
    headers: authHeader(),
  });
  return response.data;
};

const getTruckSizesBySource = async (sourceIds, signal = null) => {
  if (!sourceIds || sourceIds.length === 0) {
    return { truck_sizes: [], source_ref_ids: [], count: 0 };
  }

  const params = new URLSearchParams({
    source_ref_ids: sourceIds.join(',')
  });

  const config = { headers: authHeader() };
  if (signal) config.signal = signal;

  const response = await axios.get(API_URL + `rfq/truck-sizes/?${params}`, config);
  return response.data;
};

const getAvailableTrucks = async (signal = null) => {
  const config = { headers: authHeader() };
  if (signal) config.signal = signal;

  const response = await axios.get(API_URL + "rfq/notification-criteria/available-trucks/", config);
  return response.data;
};

const getCustomers = async (searchQuery = '', limit = 10, offset = 0, sortField = '', sortDirection = '', signal = null, sourceIds = []) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (searchQuery) {
    params.append('search', searchQuery);
  }

  if (sourceIds && sourceIds.length > 0) {
    params.append('source_id', sourceIds.join(','));
  }

  if (sortField && sortDirection) {
    params.append('sort_field', sortField);
    params.append('sort_direction', sortDirection);
  }

  const config = { headers: authHeader() };
  if (signal) {
    config.signal = signal;
  }

  const response = await axios.get(API_URL + `rfq/customers/?${params}`, config);
  return response.data;
};

const getSelectedCustomers = async (customerIds, limit = 10, offset = 0, searchQuery = '', sortField = '', sortDirection = '', signal = null) => {
  if (!customerIds || customerIds.length === 0) {
    return { results: [], count: 0 };
  }

  const params = new URLSearchParams({
    ids: customerIds.join(','),
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (searchQuery) {
    params.append('search', searchQuery);
  }

  if (sortField && sortDirection) {
    params.append('sort_field', sortField);
    params.append('sort_direction', sortDirection);
  }

  const config = { headers: authHeader() };
  if (signal) {
    config.signal = signal;
  }

  const response = await axios.get(API_URL + `rfq/customers/?${params}`, config);
  return response.data;
};

const rfqNotificationsService = {
  getRules,
  getRuleDetails,
  createRule,
  updateRule,
  deleteRule,
  getContactsWithStats,
  getSelectedContacts,
  getCustomers,
  getSelectedCustomers,
  testRule,
  getRfqSources,
  getTruckSizesBySource,
  getAvailableTrucks,
  // Schedule endpoints
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  bulkCreateSchedules,
};

export default rfqNotificationsService;